/**
 * HA-779 — what a failed card collect on a reader is called, for the operator.
 *
 * <para>The Stripe Terminal SDK answers a collect or a process with <c>{ error }</c> rather than throwing:
 * a reader timeout, a declined card and the customer pressing cancel on the reader all arrive that way, and
 * the till used to treat the end of the attempt as the end of the payment. Each is an error with a Retry; none
 * is "completed", which only the server's confirmation makes it (D-HA-24).</para>
 */
export type TerminalFailureKind =
	"timeout" | "declined" | "cancelled" | "failed";

export interface TerminalFailure {
	kind: TerminalFailureKind;
	title: string;
	message: string;
}

/** The part of the SDK's ErrorResponse["error"] this reads. */
export interface TerminalErrorLike {
	code?: string;
	message?: string;
	decline_code?: string;
}

const NEXT = "Try again, or cancel and take another payment method.";

export function describeTerminalFailure(
	stage: "collect" | "process",
	error: TerminalErrorLike,
): TerminalFailure {
	const code = (error.code ?? "").toLowerCase();
	const text = `${code} ${(error.message ?? "").toLowerCase()}`;

	if (/cancel/.test(text)) {
		return {
			kind: "cancelled",
			title: "Cancelled on the reader",
			message: `The payment was cancelled on the reader. ${NEXT}`,
		};
	}

	if (/time(d)?[ _-]?out/.test(text)) {
		return {
			kind: "timeout",
			title: "The reader timed out",
			message: `No card was read in time. ${NEXT}`,
		};
	}

	if (error.decline_code || /declin/.test(text)) {
		return {
			kind: "declined",
			title: "Card declined",
			message: `The card was declined. ${NEXT}`,
		};
	}

	return {
		kind: "failed",
		title:
			stage === "collect"
				? "The reader couldn't take the card"
				: "The card payment failed",
		message: `${error.message || "Something went wrong with the card reader."} ${NEXT}`,
	};
}

/**
 * HA-780 — a reader that did not connect. Same two outcomes the operator can act on: it took too long
 * (the SDK can leave a connect pending), or it said why not. Either way there is a Retry.
 */
export function describeConnectFailure(
	error: TerminalErrorLike,
): TerminalFailure {
	const text = `${(error.code ?? "").toLowerCase()} ${(error.message ?? "").toLowerCase()}`;

	if (/time(d)?[ _-]?out|took too long/.test(text)) {
		return {
			kind: "timeout",
			title: "The reader didn't connect in time",
			message: `Check it is switched on and close to this device. ${NEXT_CONNECT}`,
		};
	}

	return {
		kind: "failed",
		title: "Couldn't connect to the reader",
		message: `${error.message || "The reader didn't connect."} ${NEXT_CONNECT}`,
	};
}

const NEXT_CONNECT =
	"Try again, choose another reader, or cancel and take another payment method.";

/** HA-780 — how long a connect may stay pending before it is called a failure. */
export const READER_CONNECT_TIMEOUT_MS = 30_000;
